import {
  Box,
  Container,
  Divider,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Link,
  useColorMode
} from "@chakra-ui/react"
import { Link as RouterLink } from "react-router-dom"
import { Logo } from "../../../Logo"
// import { links } from './_data'
import { useAuth } from "../../../contexts/AuthContext";
import { getColor, brandConfig, getComponent } from "../../../brandConfig";

interface FooterLink {
  label: string;
  href: string;
  status?: string;
}

interface FooterLinkGroup {
  title: string;
  links: FooterLink[];
}



export const FooterWithFourColumns = () => {
  const { user, isAuthenticated } = useAuth();
  const { colorMode } = useColorMode();

  // Detailed console logging of user data
  // console.log('🦶 Footer Auth State:', {
  //   isAuthenticated,
  //   userData: {
  //     id: user?.id,
  //     name: `${user?.fName} ${user?.lName}`,
  //     email: user?.email,
  //     role: user?.role,
  //     permissions: user?.permissions,
  //     isVerifiedSeller: user?.isVerifiedSeller,
  //     businessDetails: {
  //       name: user?.businessName,
  //       registrationNumber: user?.businessRegistrationNumber
  //     },
  //     addresses: {
  //       shipping: user?.shippingAddresses,
  //       billing: user?.billingAddresses
  //     },
  //     paymentDetails: user?.paymentReceivingDetails,
  //     documents: {
  //       signature: user?.encryptedSignatureIpfsUrl
  //     },
  //     dates: {
  //       created: user?.createdAt,
  //       updated: user?.updatedAt
  //     },
  //     tenantId: user?.tenantId
  //   }
  // });

  // Streamlined footer - key evaluation flow for investors/users
  const links: FooterLinkGroup[] = [
    {
      title: "Evaluate",
      links: [
        { label: "Home", href: "/" },
        { label: "Tokenomics", href: "/tokenomics" },
        { label: "Validator vs Delegator", href: "/validator-delegator" },
        { label: "Token Sale", href: "/token-sale" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Pitch Deck", href: "/pitch-deck" },
        { label: "White Paper", href: "/white-paper" },
        { label: "Development Timeline", href: "/timeline" },
        { label: "All Resources", href: "/resources" },
      ],
    },
    {
      title: "Community",
      links: [
        { label: "Discord", href: "https://discord.com/channels/1300919183923744821/1372318769850089662" },
        { label: "Twitter/X", href: "https://twitter.com/block52xyz" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", href: "/pp" },
        { label: "Terms of Use", href: "/terms" },
        { label: "Cookie Policy", href: "/cookie" },
      ],
    },
  ]

  // Debug log for links visibility
  // console.log('🔗 Footer Links State:', {
  //   isAuthenticated,
  //   userPermissions: user?.permissions,
  //   visibleLinks: links.map(group => ({
  //     title: group.title,
  //     links: group.links.map(link => link.label)
  //   }))
  // });

  // Debug log to verify signature URL condition
  // console.log('📜 Membership Agreement State:', {
  //   hasSignature: !!user?.encryptedSignatureIpfsUrl,
  //   signatureUrl: user?.encryptedSignatureIpfsUrl,
  //   userId: user?.id,
  //   membershipLink: user?.encryptedSignatureIpfsUrl ? `/membership-certificate/${user.id}` : null
  // });

  return (<>
    <Box
      bg="rgba(10, 10, 15, 0.95)"
      borderTop="1px solid"
      borderColor="rgba(255, 255, 255, 0.08)"
      py={8}
      mt="auto" // This pushes the footer to the bottom
    >
      <Container as="footer" role="contentinfo" maxW="6xl">
        <VStack spacing={8}>
          {/* Main Footer Content - Streamlined */}
          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            gap={8}
            width="full"
            mb={8}
          >
            {links.map((group, groupIdx) => (
              <VStack key={groupIdx} align="start" spacing={4}>
                <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? getColor("text.primary", colorMode) : "white"}>
                  {group.title}
                </Text>
                <VStack align="start" spacing={2}>
                  {group.links.map((link, idx) => (
                    <Link
                      key={idx}
                      as={RouterLink}
                      to={link.href}
                      color={getComponent("footer", "linkColor", colorMode)}
                      fontSize="sm"
                      fontWeight="400"
                      transition="all 0.2s ease"
                      _hover={{
                        color: getComponent("footer", "linkHover", colorMode),
                        textDecoration: "none"
                      }}
                      opacity={link.status ? 0.5 : 1}
                      pointerEvents={link.status ? "none" : "auto"}
                    >
                      {link.label} {link.status && <Text as="span" fontSize="xs" opacity={0.6}> {link.status}</Text>}
                    </Link>
                  ))}
                </VStack>
              </VStack>
            ))}
          </SimpleGrid>

          {/* Brand & Contact Section */}
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            gap={8}
            width="full"
            pt={8}
            borderTop="1px solid"
            borderColor="rgba(255, 255, 255, 0.08)"
          >
            {/* Brand Section */}
            <VStack align="start" spacing={4}>
              <Logo />
              <Text fontSize="sm" color={getComponent("footer", "text", colorMode)} maxW="400px">
                {brandConfig.description}
              </Text>
            </VStack>

            {/* Contact Section */}
            <VStack align="start" spacing={4}>
              <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? getColor("text.primary", colorMode) : "white"}>
                Contact
              </Text>
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color={getComponent("footer", "text", colorMode)}>
                  {brandConfig.contact.businessType}
                </Text>
                <Text fontSize="sm" color={getComponent("footer", "text", colorMode)}>
                  {brandConfig.contact.industry}
                </Text>
                {brandConfig.contact.email && (
                  <Text fontSize="sm" color={getComponent("footer", "text", colorMode)}>
                    {brandConfig.contact.email}
                  </Text>
                )}
              </VStack>
            </VStack>
          </SimpleGrid>

          {/* Bottom Bar */}
          <Divider borderColor="rgba(255, 255, 255, 0.08)" />

          <HStack justify="space-between" w="full" flexWrap="wrap" spacing={4}>
            <Text fontSize="xs" color={getComponent("footer", "text", colorMode)}>
              &copy; {new Date().getFullYear()} {brandConfig.siteName}. All rights reserved.
            </Text>

            <HStack spacing={6} flexWrap="wrap">
              <Link
                as={RouterLink}
                to="/governance"
                fontSize="xs"
                color={getComponent("footer", "linkColor", colorMode)}
                _hover={{
                  color: getComponent("footer", "linkHover", colorMode),
                  textDecoration: "none"
                }}
              >
                Privacy
              </Link>
              <Link
                as={RouterLink}
                to="/governance"
                fontSize="xs"
                color={getComponent("footer", "linkColor", colorMode)}
                _hover={{
                  color: getComponent("footer", "linkHover", colorMode),
                  textDecoration: "none"
                }}
              >
                Terms
              </Link>
              <Link
                as={RouterLink}
                to="/"
                fontSize="xs"
                color={getComponent("footer", "linkColor", colorMode)}
                _hover={{
                  color: getComponent("footer", "linkHover", colorMode),
                  textDecoration: "none"
                }}
              >
                Contact
              </Link>
              <Link
                as={RouterLink}
                to="/login"
                fontSize="xs"
                color={getComponent("footer", "linkColor", colorMode)}
                opacity={0.6}
                _hover={{
                  color: getComponent("footer", "linkHover", colorMode),
                  textDecoration: "none",
                  opacity: 1
                }}
              >
                Admin
              </Link>
            </HStack>
          </HStack>
        </VStack>
      </Container>
    </Box>
  </>
  )
}